import React from 'react';

const RiskDisclaimer = () => {
  return (
    <div className="min-h-screen bg-white">
      <div className="container mx-auto px-6 py-16 max-w-4xl">
        <h1 className="text-4xl font-bold text-gray-900 mb-8">Risk Disclaimer</h1>
        <p className="text-sm text-gray-500 mb-12">Last Updated: December 2025</p>

        <div className="bg-yellow-50 border-l-4 border-yellow-600 p-6 mb-12">
          <p className="text-lg font-semibold text-gray-900 mb-2">Important Notice</p>
          <p className="text-gray-700">
            Please read this Risk Disclaimer carefully before using Comvinity's services. Use of our platform involves risks, and you should understand these risks before making business decisions based on our recommendations.
          </p>
        </div>

        <div className="prose prose-lg max-w-none space-y-8">
          <section>
            <h2 className="text-2xl font-bold text-gray-900 mb-4">1. No Guaranteed Results</h2>
            <p className="text-gray-700 leading-relaxed">
              Comvinity provides data analysis, insights, and recommendations to help optimize your Amazon business. However, we do not guarantee specific results, profit increases, or business outcomes. Success depends on many factors including:
            </p>
            <ul className="list-disc pl-6 space-y-2 text-gray-700 mt-4">
              <li>Market conditions and competition</li>
              <li>Product quality and demand</li>
              <li>Your business execution and decisions</li>
              <li>Amazon platform changes and policies</li>
              <li>External economic factors</li>
            </ul>
          </section>

          <section>
            <h2 className="text-2xl font-bold text-gray-900 mb-4">2. Business Risk</h2>
            <p className="text-gray-700 leading-relaxed">
              Operating an Amazon business involves inherent risks including but not limited to:
            </p>
            <ul className="list-disc pl-6 space-y-2 text-gray-700 mt-4">
              <li>Inventory risk and cash flow challenges</li>
              <li>Advertising spend may not generate expected returns</li>
              <li>Market competition and price pressures</li>
              <li>Account suspension or policy violations</li>
              <li>Returns, refunds, and customer disputes</li>
              <li>Supply chain disruptions</li>
            </ul>
            <p className="text-gray-700 leading-relaxed mt-4">
              You are solely responsible for all business decisions and their consequences.
            </p>
          </section>

          <section>
            <h2 className="text-2xl font-bold text-gray-900 mb-4">3. Recommendations Are Advisory Only</h2>
            <p className="text-gray-700 leading-relaxed">
              All recommendations, insights, and suggestions provided by Comvinity are advisory in nature. They are based on data analysis and algorithms, but:
            </p>
            <ul className="list-disc pl-6 space-y-2 text-gray-700 mt-4">
              <li>You are not required to follow any recommendations</li>
              <li>You should conduct your own analysis and due diligence</li>
              <li>Recommendations may not be suitable for your specific situation</li>
              <li>Past performance does not predict future results</li>
              <li>We do not provide financial, legal, or tax advice</li>
            </ul>
          </section>

          <section>
            <h2 className="text-2xl font-bold text-gray-900 mb-4">4. Data Accuracy</h2>
            <p className="text-gray-700 leading-relaxed">
              While we strive for accuracy, our platform relies on data from Amazon APIs and other sources. We do not guarantee:
            </p>
            <ul className="list-disc pl-6 space-y-2 text-gray-700 mt-4">
              <li>Complete accuracy of all data</li>
              <li>Real-time data synchronization</li>
              <li>Absence of data processing errors</li>
              <li>Compatibility with all Amazon account configurations</li>
            </ul>
            <p className="text-gray-700 leading-relaxed mt-4">
              You should verify critical information independently before making significant business decisions.
            </p>
          </section>

          <section>
            <h2 className="text-2xl font-bold text-gray-900 mb-4">5. Amazon Platform Changes</h2>
            <p className="text-gray-700 leading-relaxed">
              Amazon frequently updates its platform, policies, and algorithms. These changes may:
            </p>
            <ul className="list-disc pl-6 space-y-2 text-gray-700 mt-4">
              <li>Affect the accuracy of our recommendations</li>
              <li>Impact your business performance unpredictably</li>
              <li>Require adjustments to strategies we suggest</li>
              <li>Create temporary service disruptions</li>
            </ul>
          </section>

          <section>
            <h2 className="text-2xl font-bold text-gray-900 mb-4">6. Third-Party Dependencies</h2>
            <p className="text-gray-700 leading-relaxed">
              Our service depends on Amazon APIs and other third-party services. Disruptions, changes, or termination of these services may affect Comvinity's functionality and your ability to access data.
            </p>
          </section>

          <section>
            <h2 className="text-2xl font-bold text-gray-900 mb-4">7. Financial Considerations</h2>
            <p className="text-gray-700 leading-relaxed">
              Implementing recommendations may require:
            </p>
            <ul className="list-disc pl-6 space-y-2 text-gray-700 mt-4">
              <li>Increased advertising spend</li>
              <li>Inventory investments</li>
              <li>Pricing adjustments that affect margins</li>
              <li>Operational changes with associated costs</li>
            </ul>
            <p className="text-gray-700 leading-relaxed mt-4">
              Ensure you have adequate capital and cash flow before implementing suggestions that require financial investment.
            </p>
          </section>

          <section>
            <h2 className="text-2xl font-bold text-gray-900 mb-4">8. Professional Advice</h2>
            <p className="text-gray-700 leading-relaxed">
              Comvinity does not provide professional financial, legal, tax, or accounting advice. You should consult with qualified professionals for:
            </p>
            <ul className="list-disc pl-6 space-y-2 text-gray-700 mt-4">
              <li>Tax implications of business decisions</li>
              <li>Legal compliance and contract issues</li>
              <li>Financial planning and risk management</li>
              <li>Accounting and bookkeeping matters</li>
            </ul>
          </section>

          <section>
            <h2 className="text-2xl font-bold text-gray-900 mb-4">9. User Responsibility</h2>
            <p className="text-gray-700 leading-relaxed">
              You acknowledge and agree that:
            </p>
            <ul className="list-disc pl-6 space-y-2 text-gray-700 mt-4">
              <li>You use Comvinity at your own risk</li>
              <li>You are responsible for all decisions and actions</li>
              <li>You will not rely solely on our platform for critical business decisions</li>
              <li>You will comply with all Amazon policies and applicable laws</li>
              <li>Any losses incurred are your sole responsibility</li>
            </ul>
          </section>

          <section>
            <h2 className="text-2xl font-bold text-gray-900 mb-4">10. Limitation of Liability</h2>
            <p className="text-gray-700 leading-relaxed">
              To the maximum extent permitted by law, Comvinity and its affiliates shall not be liable for any:
            </p>
            <ul className="list-disc pl-6 space-y-2 text-gray-700 mt-4">
              <li>Business losses or lost profits</li>
              <li>Inventory losses or damages</li>
              <li>Advertising spend or wasted budget</li>
              <li>Account suspensions or restrictions</li>
              <li>Lost sales or revenue</li>
              <li>Indirect, consequential, or punitive damages</li>
            </ul>
          </section>

          <section>
            <h2 className="text-2xl font-bold text-gray-900 mb-4">11. Acknowledgment</h2>
            <p className="text-gray-700 leading-relaxed">
              By using Comvinity, you acknowledge that you have read, understood, and agreed to this Risk Disclaimer. You understand that Amazon business operations involve risk, and you accept full responsibility for your business decisions.
            </p>
          </section>

          <section>
            <h2 className="text-2xl font-bold text-gray-900 mb-4">12. Contact</h2>
            <p className="text-gray-700 leading-relaxed">
              For questions about this Risk Disclaimer, contact us at legal@comvinity.com
            </p>
          </section>
        </div>
      </div>
    </div>
  );
};

export default RiskDisclaimer;
